import React, { Fragment } from 'react';
import Context from 'managers/Context';

const DEBUG_LITERAL_NOT_FOUND = Symbol('DEBUG_LITERAL_NOT_FOUND');
const debugLiterals = {};

const hasLiteralKey = (source, literal) =>
    Boolean(source) && Object.prototype.hasOwnProperty.call(source, literal);

const normalizeLiteral = (literal) =>
    typeof literal === 'string' ? literal.split('\\n').join('\n') || literal : literal;

const resolveDebugLiteral = (literal) => {
    if (hasLiteralKey(debugLiterals, literal)) {
        return debugLiterals[literal];
    }

    return DEBUG_LITERAL_NOT_FOUND;
};

export const injectDebugLiterals = (literals = {}) => {
    if (!literals || typeof literals !== 'object' || Array.isArray(literals)) return;
    Object.assign(debugLiterals, literals);
};

export const getLiteral = (literal) => {
    const literals = Context?.store?.getState()?.literal?.literals;
    if (hasLiteralKey(literals, literal)) {
        return normalizeLiteral(literals[literal]);
    }

    const debugLiteral = resolveDebugLiteral(literal);
    if (debugLiteral !== DEBUG_LITERAL_NOT_FOUND) {
        return normalizeLiteral(debugLiteral);
    }

    return literal;
};

export const getLiteralWithParameters = (literal, params = []) => {
    let result = getLiteral(literal);
    if (params && params.length > 0 && result) {
        result = result.replace(/\%([0-9]+)\$s/g, '%@');
        if (result.includes('%@')) {
            const appearances = result.match(new RegExp('%@', 'g')).length;
            for (let i = 0; i < appearances && i < params.length; i++) {
                result = result.replace('%@', params[i]);
            }
        }
    }
    return result;
};

/**
 * Picks singular or plural literal keys based on count and returns the formatted label.
 */
export const getLiteralPluralized = (count, singularKey, pluralKey) => {
    const numericCount = Number(count);

    if (numericCount === 1) {
        return getLiteral(singularKey);
    }

    return getLiteralWithParameters(pluralKey, [numericCount]);
};

export const getLiteralWithParametersHtml = (literal, params = [], customizeParameter) => {
    let result = getLiteral(literal);
    if (params && params.length > 0 && result) {
        result = result.replace(/\%([0-9]+)\$s/g, '%@');
        if (result.includes('%@')) {
            const appearances = result.match(new RegExp('%@', 'g'));
            let lastIndex = 0;
            let resultParts = [];
            appearances.forEach((app, index) => {
                const currentIndex = result.indexOf('%@', lastIndex);
                resultParts.push(result.slice(lastIndex, currentIndex));
                resultParts.push(
                    customizeParameter ? customizeParameter(params[index], index) : params[index],
                );
                lastIndex = currentIndex + 2;
            });
            if (lastIndex < result.length) {
                resultParts.push(result.slice(lastIndex, result.length));
            }

            return (
                <Fragment>
                    {resultParts.map((part, index) => (
                        <Fragment key={index}>{part}</Fragment>
                    ))}
                </Fragment>
            );
        }
    }
    return result;
};
